import { getSchemaText } from '@principle-theorem/editor';
import {
  IAppointment,
  ICalendarEvent,
  IEventable,
  IPractice,
  IScheduleSummaryEvent,
  IScheduleSummaryEventable,
  MentionResourceType,
  PrincipleCreatedBy,
  isAppointment,
  isCalendarEvent,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  WithRef,
  asDocRef,
  isDocRef,
  isEnumValue,
  isWithRef,
  sortTimestampAsc,
  toTimePeriod,
  where,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import { Appointment } from '../appointment/appointment';
import { TreatmentStep } from '../clinical-charting/treatment/treatment-step';
import { CalendarEvent } from '../event/calendar-event';
import { CalendarEventTemplate } from '../event/calendar-event-template';

export class ScheduleSummary {
  static async toSummaryEvent<T extends object>(
    ref: DocumentReference<T>,
    eventable?: IEventable<T>,
    isBlocking: boolean = true
  ): Promise<IScheduleSummaryEvent<T> | undefined> {
    if (!eventable?.event) {
      return;
    }

    const label = await getEventLabel(eventable);
    const pinnedNotes = await getPinnedNotes(eventable);

    if (
      isAppointment(eventable) &&
      isWithRef<IAppointment>(eventable) &&
      isDocRef<IAppointment>(ref)
    ) {
      return getAppointmentSummary(
        eventable,
        ref,
        isBlocking,
        label,
        pinnedNotes
      ) as unknown as IScheduleSummaryEvent<T>;
    }

    if (isCalendarEvent(eventable) && isDocRef<ICalendarEvent>(ref)) {
      return getCalendarEventSummary(
        eventable,
        ref,
        isBlocking,
        label,
        pinnedNotes
      ) as unknown as IScheduleSummaryEvent<T>;
    }
  }

  static async getCalendarEvent(
    event: IScheduleSummaryEventable<ICalendarEvent>,
    practice: WithRef<IPractice>
  ): Promise<WithRef<ICalendarEvent> | ICalendarEvent | undefined> {
    if (event.ref) {
      return Firestore.getDoc(asDocRef<ICalendarEvent>(event.ref));
    }
    const { from, to } = event.event;
    const timezone = practice.settings.timezone;
    const timePeriod = toTimePeriod(from, to, timezone);
    const scheduleRef = event.metadata.scheduleRef;
    const scheduleEvent = scheduleRef
      ? await Firestore.getDoc(scheduleRef)
      : undefined;
    return scheduleEvent
      ? CalendarEventTemplate.toCalendarEvent(
          scheduleEvent.item,
          timePeriod,
          scheduleRef
        )
      : undefined;
  }

  static async getSummaryFromEventable<T extends object>(
    eventable?: IEventable<T>,
    isBlocking = true
  ): Promise<IScheduleSummaryEventable<T> | undefined> {
    if (!eventable || !eventable.ref || !eventable.event) {
      return;
    }

    const summaryEvent = await ScheduleSummary.toSummaryEvent<T>(
      eventable.ref,
      eventable,
      isBlocking
    );

    if (!summaryEvent) {
      return;
    }

    return {
      ...summaryEvent,
      uid: summaryEvent.ref.id,
    };
  }
}

function getAppointmentSummary(
  appointment: IEventable & WithRef<IAppointment>,
  ref: DocumentReference<IAppointment>,
  isBlocking: boolean,
  label: string,
  pinnedNotes: string[]
): IScheduleSummaryEvent<IAppointment> {
  const createdBy =
    !isString(appointment.createdBy) &&
    appointment.createdBy &&
    isEnumValue(MentionResourceType, appointment.createdBy.type)
      ? (appointment.createdBy as PrincipleCreatedBy)
      : undefined;

  return {
    ref,
    event: appointment.event,
    isBlocking,
    metadata: {
      createdBy,
      label,
      pinnedNotes,
      tags: appointment.tags,
      patientRef: Appointment.patientRef(appointment),
      status: appointment.status,
      dependencies: appointment.dependencies,
      categoryRef: TreatmentStep.defaultDisplayRef(
        appointment.treatmentPlan.treatmentStep.display
      ),
      treatmentPlanName: appointment.treatmentPlan.name,
      treatmentStepName: appointment.treatmentPlan.treatmentStep.name,
      appointmentRequestRef: appointment.appointmentRequestRef,
      deposit: appointment.deposit?.metadata,
    },
  };
}

function getCalendarEventSummary(
  calendarEvent: ICalendarEvent,
  ref: DocumentReference<ICalendarEvent>,
  isBlocking: boolean,
  label: string,
  pinnedNotes: string[]
): IScheduleSummaryEvent<ICalendarEvent> {
  return {
    ref,
    event: calendarEvent.event,
    isBlocking,
    metadata: {
      createdBy: CalendarEvent.getCreatedBy(calendarEvent),
      label,
      tags: calendarEvent.eventTags,
      pinnedNotes,
      scheduleRef: calendarEvent.scheduleRef,
      locked: calendarEvent.locked,
    },
  };
}

async function getEventLabel(eventable: IEventable): Promise<string> {
  if (isCalendarEvent(eventable)) {
    return getSchemaText(eventable.title);
  }
  if (!isWithRef<IAppointment>(eventable) || !isAppointment(eventable)) {
    return '';
  }
  return (await Appointment.patient(eventable)).name;
}

async function getPinnedNotes(eventable: IEventable): Promise<string[]> {
  if (isAppointment(eventable) && isWithRef<IAppointment>(eventable)) {
    const interactions = await Firestore.getDocs(
      Appointment.interactionCol(eventable),
      where('pinned', '==', true)
    );

    interactions.sort((interactionA, interactionB) =>
      sortTimestampAsc(interactionA.createdAt, interactionB.createdAt)
    );
    return interactions
      .filter((interaction) => !interaction.deleted)
      .map((interaction) => getSchemaText(interaction.content));
  }
  if (!isWithRef(eventable)) {
    return [];
  }
  if (isCalendarEvent(eventable)) {
    return [getSchemaText(eventable.notes)];
  }

  return [];
}
